// Setting the real times of a day the system closed (owner, 2026-10-13): the
// person's direct manager from My space, HR from the person's Hours tab. One
// dialog for both. The server decides who may and refuses the rest
// (20261013100000_fix_auto_closed_day.sql); this only says it in words.
import { useEffect, useState } from 'react';
import { Button, Mono } from './primitives';
import { Card } from './card';
import { Dialog } from './dialog';
import { Field, TextArea, TextInput } from './field';
import { InlineStatus, LoadError, useToast } from './feedback';
import { DecisionRow } from './requests';
import { EditDayDialog } from './editDay';
import { fmtDayKey, toOrgWallTime } from '../lib/dates';
import { fmtDuration } from '../lib/duration';
import { fixAutoClosedDay, listDaysToFix, type AutoClosedDay } from '../lib/autoClosedDays';

/** The day to fix, as the dialog needs it. */
export type DayToFix = {
  entryId: string;
  name: string;
  /** 'YYYY-MM-DD', the org day. */
  workDate: string;
  /** ISO: when they checked in. */
  checkIn: string;
};

const dayLabel = (iso: string) => fmtDayKey(iso, { weekday: true });
// "09:05" on the org's clock, or '' when the instant isn't on that day.
const wall = (iso: string, day: string, tz: string) => {
  const w = toOrgWallTime(iso, tz);
  return w.slice(0, 10) === day ? w.slice(11, 16) : '';
};

export function FixDayDialog({
  day,
  tz,
  onClose,
  onFixed,
}: {
  day: DayToFix;
  tz: string;
  onClose: () => void;
  onFixed: () => void;
}) {
  const toast = useToast();
  // Check-in starts at what was recorded; check-out is the question: the
  // system's close time is never the answer.
  const [inTime, setInTime] = useState(wall(day.checkIn, day.workDate, tz));
  const [outTime, setOutTime] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<{
    at: 'in' | 'out' | 'reason' | 'form';
    message: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const errorAt = (at: 'in' | 'out' | 'reason') =>
    problem?.at === at ? problem.message : undefined;

  const save = async () => {
    if (busy) return;
    if (!inTime) return setProblem({ at: 'in', message: 'When did they check in?' });
    if (!outTime) return setProblem({ at: 'out', message: 'When did they leave?' });
    if (outTime <= inTime)
      return setProblem({ at: 'out', message: 'Check-out has to be after check-in.' });
    if (!reason.trim())
      return setProblem({ at: 'reason', message: `Say why: ${day.name} sees it with the change.` });
    setBusy(true);
    setProblem(null);
    const { error } = await fixAutoClosedDay({
      entryId: day.entryId,
      checkIn: inTime,
      checkOut: outTime,
      reason: reason.trim(),
    });
    setBusy(false);
    if (error) return setProblem({ at: 'form', message: error.message });
    toast.push({
      tone: 'success',
      message: `Set ${day.name}’s ${dayLabel(day.workDate)} to ${inTime}–${outTime}. They’ve been told.`,
    });
    onFixed();
    onClose();
  };

  return (
    <Dialog
      open
      title={`Set ${day.name}’s times`}
      onClose={onClose}
      dirty={reason.trim() !== '' || outTime !== ''}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" icon="check" loading={busy} onClick={() => void save()}>
            Set times
          </Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        <p style={{ margin: 0, fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' }}>
          {dayLabel(day.workDate)}. They didn’t check out, so the day was closed for them. Set when
          they actually came and left. Their hours, attendance and overtime follow.
        </p>
        <div style={{ display: 'flex', gap: 'var(--hf-space-3)', alignItems: 'flex-start' }}>
          <Field label="Checked in" error={errorAt('in')} style={{ flex: '1 1 0' }}>
            <TextInput type="time" mono value={inTime} onChange={setInTime} />
          </Field>
          <Field label="Checked out" error={errorAt('out')} style={{ flex: '1 1 0' }}>
            <TextInput type="time" mono value={outTime} onChange={setOutTime} />
          </Field>
        </div>
        <Field
          label="Why"
          required
          error={errorAt('reason')}
          hint={`${day.name} sees this with the change, and it is kept on record.`}
        >
          <TextArea
            value={reason}
            onChange={setReason}
            placeholder="e.g. Left at 6, confirmed on Slack"
          />
        </Field>
        {problem?.at === 'form' && <InlineStatus tone="error">{problem.message}</InlineStatus>}
      </div>
    </Dialog>
  );
}

/**
 * My space, for someone people report to: their auto-closed days, each with
 * Set times. Renders nothing when there are none. No attention badge, like the
 * compensated-hours queue beside it: /me has no module to hang one on, and the
 * auto-close notice already brought them here.
 */
export function TeamDaysToFix({ tz, editDay }: { tz: string; editDay?: boolean }) {
  const [rows, setRows] = useState<AutoClosedDay[]>([]);
  // Edit day on (policy.editDay.rollout): the whole-day dialog instead.
  const [editing, setEditing] = useState<AutoClosedDay | null>(null);
  const [failed, setFailed] = useState(false);
  const [fixing, setFixing] = useState<DayToFix | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    void listDaysToFix().then(({ data, error }) => {
      if (!live) return;
      setFailed(!!error);
      setRows(data);
    });
    return () => {
      live = false;
    };
  }, [nonce]);

  if (!rows.length && !failed) return null;
  return (
    <Card label="Days the system closed for your team">
      {failed ? (
        <LoadError what="your team’s auto-closed days" onRetry={() => setNonce((n) => n + 1)} />
      ) : (
        rows.map((r) => {
          const name = r.full_name ?? 'Someone';
          return (
            <DecisionRow
              key={r.entry_id}
              flush
              name={name}
              when={dayLabel(r.work_date)}
              what={
                <>
                  <span style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>
                    Checked in {wall(r.check_in, r.work_date, tz) || '—'}, no check-out
                  </span>
                  <Mono>{fmtDuration(r.seconds, 'hm')} counted</Mono>
                </>
              }
              aside={
                <Button
                  size="sm"
                  variant="secondary"
                  className="hf-hit"
                  onClick={() =>
                    editDay
                      ? setEditing(r)
                      : setFixing({
                          entryId: r.entry_id,
                          name,
                          workDate: r.work_date,
                          checkIn: r.check_in,
                        })
                  }
                >
                  {editDay ? 'Edit day' : 'Set times'}
                </Button>
              }
            />
          );
        })
      )}
      {fixing && (
        <FixDayDialog
          day={fixing}
          tz={tz}
          onClose={() => setFixing(null)}
          onFixed={() => setNonce((n) => n + 1)}
        />
      )}
      {editing && (
        <EditDayDialog
          profileId={editing.profile_id}
          name={editing.full_name ?? 'Someone'}
          date={editing.work_date}
          onClose={() => setEditing(null)}
          onDone={() => setNonce((n) => n + 1)}
        />
      )}
    </Card>
  );
}
